import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [phoneError, setPhoneError] = useState('');
  const [phoneValue, setPhoneValue] = useState('');

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Remove any non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    
    // Limit to 11 digits
    const limitedValue = numericValue.slice(0, 11);
    
    setPhoneValue(limitedValue);
    
    // Validation feedback
    if (numericValue.length > 11) {
      setPhoneError('Maximum 11 digits allowed');
    } else if (value !== numericValue) {
      setPhoneError('Only numbers are allowed');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate phone number if provided
    if (phoneValue && (phoneValue.length > 11 || !/^\d+$/.test(phoneValue))) {
      setPhoneError('Please enter a valid phone number (max 11 digits)');
      return;
    }
    
    // Placeholder - will be integrated later
    toast.success('Thank you! We will contact you shortly.');
    e.target.reset();
    setPhoneValue('');
    setPhoneError('');
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Trademark Credentials | Get Expert Trademark Advice</title>
        <meta name="description" content="Contact Trademark Credentials for expert trademark registration assistance. Available Mon-Fri 9AM-6PM EST. Email: info@trademarkcredentials.com | Phone: +1 (877) 410-0136" />
        <meta name="keywords" content="contact trademark attorney, trademark consultation, brand protection help, USPTO assistance" />
      </Helmet>
    <div className="min-h-screen">
      {/* Hero Section with Background */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1497366754035-f200968a6e72)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/95 via-purple-800/90 to-purple-700/95"></div>
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get In <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">Touch</span>
          </h1>
          <p className="text-xl text-purple-100 max-w-3xl mx-auto">
            Have questions? We're here to help. Reach out to our team for expert trademark advice.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pt-20 pb-4 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                  <Input type="text" required placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <Input type="email" required placeholder="john@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <Input 
                    type="tel" 
                    placeholder="+1 (877) 410-0136"
                    value={phoneValue}
                    onChange={handlePhoneChange}
                    className={phoneError ? 'border-red-500' : ''}
                    maxLength={11}
                  />
                  {phoneError && (
                    <p className="text-red-500 text-sm mt-1">{phoneError}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <Input type="text" placeholder="How can we help you?" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <Textarea required rows={6} placeholder="Tell us about your trademark needs..." />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Send Message
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Our team is ready to assist you with all your trademark registration needs. Contact us through any of the following channels.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-6 bg-gradient-to-br from-purple-50/30 to-purple-100/20 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1">Email Us</h3>
                    <p className="text-gray-600">info@trademarkcredentials.com</p>
                    <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-gradient-to-br from-purple-50/30 to-purple-100/20 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1">Call Us</h3>
                    <p className="text-gray-600">+1 (877) 410-0136</p>
                    <p className="text-sm text-gray-500 mt-1">Mon-Fri, 9AM-6PM EST</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-gradient-to-br from-purple-50/30 to-purple-100/20 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1">Visit Us</h3>
                    <p className="text-gray-600">9000 Sunset Blvd, West Hollywood, CA 90069</p>
                    <p className="text-sm text-gray-500 mt-1">Serving clients nationwide</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-gradient-to-br from-purple-50/30 to-purple-100/20 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1">Business Hours</h3>
                    <p className="text-gray-600">Monday - Friday: 9AM - 6PM EST</p>
                    <p className="text-sm text-gray-500 mt-1">Closed on weekends and holidays</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contact;
